import {
	AlertFilled,
	CheckOutlined,
	MessageFilled,
	PlusSquareFilled,
	UserOutlined,
} from "@ant-design/icons"
import { Avatar } from "components/Avatar/Avatar"
import { Image } from "components/Image/Image"
import { useModalNavigate } from "hooks/useModalNavigate"
import { WeightIcon } from "icons/WeightIcon"
import { PatientItem } from "interface/patientType"
import { useCallback } from "react"
import { useNavigate, useParams } from "react-router"
import { useUserRole } from "services/users"
import { cn } from "styles/cn"
import {
	dateFormatter,
	formatAsTime,
	getTimeZone,
	isToday,
	isYesterday,
} from "utils/date"
import { kgToLbs } from "utils/index"

type PatientItemProps = {
	item: PatientItem
}
export function PatientListItem({ item }: PatientItemProps) {
	const {
		lastCaptureAt,
		lastWeightKgf,
		lastThumbImageUrl,
		newDataPointsCount,
		unreadCommunicationDataCount,
		supportTicketUpdateCount,
		authoredOverdueEscalationCount,
		isAssignedToCallerAndUnopened,
		openEscalationCount,
	} = item
	const { scopeId } = useParams()
	const navigate = useNavigate()
	const modalNavigate = useModalNavigate()
	const { data: { isProviderAnon } = {} } = useUserRole()
	const patientName = isProviderAnon ? "Anonymous patient" : item.fullName
	const handleClickPatientItem = useCallback(() => {
		navigate(`/${scopeId}/patients/${item.id}`)
	}, [item.id, navigate, scopeId])
	const handleClickPatientItemData = useCallback(() => {
		modalNavigate(
			`/${scopeId}/patients/${item.id}/m/screenings/${item.lastDataId}`,
		)
	}, [item.id, item.lastDataId, modalNavigate, scopeId])

	return (
		<li
			tabIndex={0}
			className="focus-outline grid cursor-pointer grid-cols-1 items-center gap-2 rounded-xl border border-mint-20 bg-white p-4 hover:border-green-60 md:grid-cols-2 lg:grid-cols-3"
			aria-label={
				!isProviderAnon ? `Patient ${patientName}` : "Anonymous patient"
			}
			onClick={handleClickPatientItem}
			onKeyDown={(e) => {
				if (e.key === "Enter" || e.key === " ") {
					e.preventDefault()
					handleClickPatientItem()
				}
			}}
		>
			<div className="flex items-center justify-start gap-2">
				<Avatar
					className="size-11 min-h-11 min-w-11 text-2xl lg:size-13 lg:min-h-13 lg:min-w-13"
					icon={isProviderAnon ? <UserOutlined aria-hidden /> : undefined}
				>
					{patientName}
				</Avatar>
				<div className="min-w-0 flex-col space-y-1">
					{!isProviderAnon && (
						<p className="truncate text-lg font-semibold">{patientName}</p>
					)}
					<div className="flex flex-col justify-start gap-1 text-base text-blue-60">
						{authoredOverdueEscalationCount > 0 && (
							<div className="flex gap-1 text-red-60">
								<AlertFilled aria-hidden />
								<span className="truncate">{`${authoredOverdueEscalationCount} escalation(s) open for >48h`}</span>
							</div>
						)}
						{!authoredOverdueEscalationCount &&
							isAssignedToCallerAndUnopened &&
							openEscalationCount > 0 && (
								<div className="flex gap-1 text-orange-60">
									<AlertFilled aria-hidden />
									<span className="truncate">{`${openEscalationCount} escalation(s) open`}</span>
								</div>
							)}
						{!authoredOverdueEscalationCount &&
							!isAssignedToCallerAndUnopened &&
							openEscalationCount > 0 && (
								<div className="flex gap-1 text-navy-60">
									<AlertFilled aria-hidden />
									<span className="truncate">{`${openEscalationCount} escalation(s) open`}</span>
								</div>
							)}
						{unreadCommunicationDataCount > 0 && (
							<div className="flex gap-1">
								<MessageFilled aria-hidden />
								<span className="truncate">{`${unreadCommunicationDataCount} unread message(s)`}</span>
							</div>
						)}
						{supportTicketUpdateCount > 0 && (
							<div className="flex gap-1">
								<PlusSquareFilled aria-hidden />
								<span className="truncate">{`${supportTicketUpdateCount} support request update(s)`}</span>
							</div>
						)}
					</div>
				</div>
			</div>

			<div className="flex items-center justify-start gap-2">
				{!!lastCaptureAt ? (
					<>
						<button
							className={cn(
								"focus-outline relative size-14 rounded-lg hover:scale-200 hover:shadow-[0_8px_20px_4px_rgba(141,176,149,0.3)] hover:transition-transform",
								!!lastThumbImageUrl && "cursor-pointer bg-navy-60",
							)}
							{...(!!lastThumbImageUrl && {
								["aria-label"]: `Open patient ${patientName}'s latest screening`,
								onClick: (e) => {
									e.stopPropagation()
									handleClickPatientItemData()
								},
								onKeyDown: (e) => {
									if (e.key === "Enter" || e.key === " ") {
										e.stopPropagation()
										handleClickPatientItemData()
									}
								},
							})}
						>
							{!lastThumbImageUrl && (
								<WeightIcon.Null
									aria-hidden
									className="size-full text-navy-60"
								/>
							)}

							{!!lastThumbImageUrl && (
								<Image
									src={lastThumbImageUrl}
									className="rounded-lg object-cover"
								/>
							)}
						</button>
						<div className="flex min-w-0 flex-col gap-1">
							<p className="truncate text-lg font-semibold">
								{dateFormatter(lastCaptureAt, { needSimplify: true })}
								{(isToday(lastCaptureAt) || isYesterday(lastCaptureAt)) &&
									`, ${formatAsTime(lastCaptureAt)}, ${getTimeZone()}`}
							</p>
							<div className="flex items-center justify-start">
								{!!newDataPointsCount ? (
									<p className="flex items-center justify-start gap-1 rounded-2xl bg-blue-0 px-2 text-blue-60">
										{newDataPointsCount} new screenings
									</p>
								) : (
									<p className="flex items-center justify-start gap-1 rounded-2xl bg-green-20 px-2 text-green-100">
										<CheckOutlined aria-hidden />
										Reviewed
									</p>
								)}
							</div>
						</div>
					</>
				) : (
					"--"
				)}
			</div>

			<div className="flex min-w-0 items-center justify-start gap-2 md:-col-end-1">
				{!!lastCaptureAt ? (
					<>
						<div className="grid size-11 place-items-center rounded-xl bg-grey-10">
							<WeightIcon aria-hidden className="size-6 fill-grey-80" />
						</div>

						<p className="truncate text-lg font-semibold">
							{kgToLbs(lastWeightKgf)}
						</p>
					</>
				) : (
					"--"
				)}
			</div>
		</li>
	)
}
